/* EZ Leave follows Blueprint: create somebody when they are enrolled, close them
 * when they are wound down.
 *
 * A sibling of TrainingScopeClient and for the same reasons: GraphClient is bound
 * to Blueprint's OWN site in its constructor, and this reads and writes lists on
 * a different one, as the signed-in administrator. Their SharePoint permissions
 * are the permissions, so there is no service identity to grant and nothing here
 * can do more than the person pressing the button could do by hand.
 *
 * WHY THE JOINS ARE IN MEMORY
 *
 * Graph returns a person column as a bare lookup id and refuses to filter on it,
 * so the lists are read whole and matched here. Measured on the live site that is
 * 111 employees and about 120 user rows: two small reads, not a scan.
 *
 * WHAT WILL GO WRONG, AND WHAT IT DOES
 *
 * A person SharePoint has not seen on the EZ Leave site yet has no lookup id, so
 * the account column cannot be set. That is fine: EZ Leave finds a person by their
 * sign-in name, which the record carries, and does not need the column to let them
 * in. The same is true of a manager who has never opened the app — the manager is
 * left blank and said so, rather than guessed, and the next run fills it.
 *
 * Nothing here throws for a business reason. Every outcome comes back as words,
 * because the caller's job is to show them, and the enrol button must never fail
 * because EZ Leave could not be reached.
 */

import { GraphError, type TokenProvider } from '../sharepoint/GraphClient';
import {
  atMidday,
  findEmployee,
  newRecordFields,
  stampFor,
  type IEmployeeRow,
  type ILeaveSource,
} from '../../lib/leaveRecord';

const GRAPH = 'https://graph.microsoft.com/v1.0';

/** EZ Leave's site, addressed by path so no id has to be configured. */
export const LEAVE_SITE_PATH = 'hirds.sharepoint.com:/sites/HirdLeaveManager';

export type LeaveOutcomeKind =
  | 'created'   // a new record was made
  | 'exists'    // already there; nothing needed doing
  | 'linked'    // an older record was found by name and stamped with their ids
  | 'closed'    // marked as a leaver
  | 'not-found' // nothing to close
  | 'skipped'   // could not tell who this is; nothing was written
  | 'failed';   // EZ Leave could not be reached or refused the write

export interface ILeaveOutcome {
  kind: LeaveOutcomeKind;
  /** One sentence for the person pressing the button. */
  message: string;
  /** Things that were left blank or need a person: no manager, no department. */
  problems: string[];
}

interface IUserRow { id: string; mail: string; login: string }

export class LeaveSyncClient {
  private siteIdCache?: string;
  private listIdCache = new Map<string, string>();

  constructor(
    private readonly getToken: TokenProvider,
    private readonly scopes: string[] = ['Sites.ReadWrite.All'],
    private readonly sitePath: string = LEAVE_SITE_PATH,
  ) {}

  private async request<T>(url: string, init: RequestInit = {}): Promise<T> {
    const token = await this.getToken(this.scopes);
    const res = await fetch(url.startsWith('http') ? url : `${GRAPH}${url}`, {
      ...init,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...init.headers,
      },
    });
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      const detail = (body as { error?: { message?: string } } | null)?.error?.message;
      throw new GraphError(
        detail ? `Graph ${res.status}: ${detail}` : `Graph ${res.status} on ${url}`,
        res.status,
        body,
      );
    }
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  private async siteId(): Promise<string> {
    if (!this.siteIdCache) {
      const r = await this.request<{ id: string }>(`/sites/${this.sitePath}?$select=id`);
      this.siteIdCache = r.id;
    }
    return this.siteIdCache;
  }

  private async listId(title: string): Promise<string> {
    const cached = this.listIdCache.get(title);
    if (cached) return cached;
    const site = await this.siteId();
    const r = await this.request<{ id: string }>(`/sites/${site}/lists/${encodeURIComponent(title)}?$select=id`);
    this.listIdCache.set(title, r.id);
    return r.id;
  }

  private async employees(): Promise<IEmployeeRow[]> {
    const site = await this.siteId();
    const list = await this.listId('Employees');
    const r = await this.request<{ value: { id: string; fields: IEmployeeRow['fields'] }[] }>(
      `/sites/${site}/lists/${list}/items?$expand=fields($select=Title,Upn,EntraId,Email,Status,LeaverDate)&$top=999`,
    );
    return r.value.map((i) => ({ id: i.id, fields: i.fields ?? {} }));
  }

  private async departments(): Promise<Map<string, string>> {
    const site = await this.siteId();
    const list = await this.listId('Departments');
    const r = await this.request<{ value: { id: string; fields: { Title?: string } }[] }>(
      `/sites/${site}/lists/${list}/items?$expand=fields($select=Title)&$top=200`,
    );
    const map = new Map<string, string>();
    r.value.forEach((i) => {
      const t = (i.fields.Title || '').trim().toLowerCase();
      if (t) map.set(t, i.id);
    });
    return map;
  }

  /** Everyone SharePoint knows on this site: lookup id by mailbox and by sign-in
   *  name. The account name column is not always exposed, so it is asked for
   *  and, if refused, done without — the mailbox match alone still serves most. */
  private async users(): Promise<IUserRow[]> {
    const site = await this.siteId();
    const base = `/sites/${site}/lists/${encodeURIComponent('User Information List')}/items?$top=999&$expand=fields`;
    type Raw = { value: { fields: { id?: string; EMail?: string; Name?: string } }[] };
    let r: Raw;
    try {
      r = await this.request<Raw>(`${base}($select=id,EMail,Name)`);
    } catch {
      r = await this.request<Raw>(`${base}($select=id,EMail)`);
    }
    return r.value
      .filter((i) => !!i.fields.id)
      .map((i) => ({
        id: String(i.fields.id),
        mail: (i.fields.EMail || '').trim().toLowerCase(),
        /* i:0#.f|membership|rob.blanchard@hird.co.uk -> rob.blanchard@hird.co.uk */
        login: ((i.fields.Name || '').split('|').pop() || '').trim().toLowerCase(),
      }));
  }

  private lookupId(users: IUserRow[], ...keys: (string | undefined)[]): string | undefined {
    const wanted = keys.map((k) => (k || '').trim().toLowerCase()).filter(Boolean);
    for (const k of wanted) {
      const hit = users.find((u) => u.mail === k || u.login === k);
      if (hit) return hit.id;
    }
    return undefined;
  }

  /** Make sure this person has an EZ Leave record. Never throws. */
  async onboard(p: ILeaveSource): Promise<ILeaveOutcome> {
    try {
      const [rows, depts, users] = await Promise.all([this.employees(), this.departments(), this.users()]);
      const found = findEmployee(rows, p);

      if (found && 'ambiguous' in found) {
        return {
          kind: 'skipped',
          message: `EZ Leave was not changed: ${found.ambiguous}.`,
          problems: ['Tidy the duplicate in EZ Leave, then try again.'],
        };
      }

      const site = await this.siteId();
      const list = await this.listId('Employees');

      if (found && found.row.fields.Status === 'Leaver') {
        /* Not touched, not even to stamp it: a leaver's record is history. */
        return {
          kind: 'exists',
          message: 'They already have an EZ Leave record, and it is marked as a leaver.',
          problems: ['Check whether they are returning; if so, EZ Leave needs them reinstating by hand.'],
        };
      }

      if (found) {
        const stamp = stampFor(found.row, p);
        if (Object.keys(stamp).length > 0) {
          await this.request(`/sites/${site}/lists/${list}/items/${found.row.id}/fields`, {
            method: 'PATCH',
            body: JSON.stringify(stamp),
          });
        }
        const linked = found.by === 'name';
        return {
          kind: linked ? 'linked' : 'exists',
          message: linked
            ? 'EZ Leave already had a record under this name, and it is now linked to their account.'
            : 'They already have an EZ Leave record.',
          problems: [],
        };
      }

      const problems: string[] = [];

      const departmentId = p.department ? depts.get(p.department.trim().toLowerCase()) : undefined;
      if (!p.department) problems.push('Blueprint has no department for them, so none was set.');
      else if (!departmentId) problems.push(`"${p.department}" is not on EZ Leave's department list, so none was set.`);

      let managerLookupId: string | undefined;
      if (!p.manager) problems.push('No manager was set: Blueprint does not say who it is.');
      else {
        managerLookupId = this.lookupId(users, p.manager.mail, p.manager.upn);
        if (!managerLookupId) {
          problems.push(`No manager was set: ${p.manager.name} has not used EZ Leave yet, so SharePoint cannot name them. Run the sync once they have.`);
        }
      }

      if (!p.depot) problems.push('Blueprint has no depot for them, so no location was set.');

      const fields = newRecordFields(p, {
        departmentId,
        managerLookupId,
        accountLookupId: this.lookupId(users, p.upn, p.mail),
      });
      await this.request(`/sites/${site}/lists/${list}/items`, {
        method: 'POST',
        body: JSON.stringify({ fields }),
      });

      return {
        kind: 'created',
        message: 'Added to EZ Leave. HR need to add their working pattern and entitlement before they can book leave.',
        problems,
      };
    } catch (e) {
      return {
        kind: 'failed',
        message: `EZ Leave could not be updated: ${(e as Error).message}`,
        problems: ['Their onboarding is complete. Add them to EZ Leave by running the sync, or by hand.'],
      };
    }
  }

  /** Mark this person as a leaver in EZ Leave. Never throws. */
  async offboard(p: ILeaveSource, lastDay: string): Promise<ILeaveOutcome> {
    try {
      const rows = await this.employees();
      const found = findEmployee(rows, p);

      if (found && 'ambiguous' in found) {
        return {
          kind: 'skipped',
          message: `EZ Leave was not changed: ${found.ambiguous}.`,
          problems: ['Mark them as a leaver in EZ Leave by hand.'],
        };
      }
      if (!found) {
        return { kind: 'not-found', message: 'They have no EZ Leave record, so there was nothing to close.', problems: [] };
      }
      if (found.row.fields.Status === 'Leaver') {
        return { kind: 'exists', message: 'They are already marked as a leaver in EZ Leave.', problems: [] };
      }

      const site = await this.siteId();
      const list = await this.listId('Employees');
      await this.request(`/sites/${site}/lists/${list}/items/${found.row.id}/fields`, {
        method: 'PATCH',
        body: JSON.stringify({ Status: 'Leaver', LeaverDate: atMidday(lastDay), ...stampFor(found.row, p) }),
      });
      return {
        kind: 'closed',
        message: `Marked as a leaver in EZ Leave, last day ${lastDay}. Their leaver pay is worked out from there.`,
        problems: [],
      };
    } catch (e) {
      return {
        kind: 'failed',
        message: `EZ Leave could not be updated: ${(e as Error).message}`,
        problems: ['Their offboarding is complete. Mark them as a leaver in EZ Leave by hand.'],
      };
    }
  }
}
